import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";

export const createTradeRequest = async (receiverId: number | string, message: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) throw new Error('Not authenticated');

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) throw new Error('Profile not found');

    const { data, error } = await supabase
      .from('trade_requests')
      .insert({
        requester_id: profile.id,
        receiver_id: Number(receiverId),
        message: message
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating trade request:', error);
    throw error;
  }
};

/**
 * Alias semântico para solicitação de contato
 */
export const createContactRequest = createTradeRequest;

/**
 * Busca as cartas que um usuário possui para negociação (offerlist ou coleção)
 */
export const getUserOfferCards = async (userId: number | string): Promise<any[]> => {
  try {
    const { data: offerData, error } = await supabase
      .from('user_offerlist')
      .select('id, card_id, cards(id, name, image_url, set_code, game_id)')
      .eq('user_id', Number(userId));

    if (!error && offerData && offerData.length > 0) {
      return offerData
        .map((item: any) => item.cards)
        .filter(Boolean);
    }

    // Fallback: se não tiver cartas na offerlist, busca até 30 cartas da coleção do usuário
    const { data: colData } = await supabase
      .from('user_collection')
      .select('id, card_id, cards(id, name, image_url, set_code, game_id)')
      .eq('user_id', Number(userId))
      .limit(30);

    return (colData || [])
      .map((item: any) => item.cards)
      .filter(Boolean);
  } catch (err) {
    console.error('Error fetching user offer cards:', err);
    return [];
  }
};


export const getTradeRequests = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];

    const profile = await getUserByAuthId(session.user.id);
    if (!profile) return [];

    // Fetch trade requests
    const { data: rawRequests, error: rawError } = await supabase
      .from('trade_requests')
      .select('*')
      .or(`requester_id.eq.${profile.id},receiver_id.eq.${profile.id}`)
      .order('created_at', { ascending: false });

    if (rawError) throw rawError;
    if (!rawRequests || rawRequests.length === 0) return [];

    // Fetch safe users via get_safe_users RPC (due to RLS on users table)
    let usersMap = new Map<number, any>();
    try {
      const { data: safeUsers, error: safeErr } = await (supabase.rpc('get_safe_users') as any);
      if (!safeErr && safeUsers) {
        usersMap = new Map(safeUsers.map((u: any) => [Number(u.id), u]));
      }
    } catch (e) {
      console.warn('Erro ao chamar get_safe_users em getTradeRequests:', e);
    }

    return rawRequests.map(r => ({
      ...r,
      requester: usersMap.get(Number(r.requester_id)) || null,
      receiver: usersMap.get(Number(r.receiver_id)) || null
    }));
  } catch (error) {
    console.error('Error fetching trade requests:', error);
    return [];
  }
};


export const updateTradeRequestStatus = async (
  requestId: number | string, 
  status: 'pending' | 'accepted' | 'declined' | 'rejected' | 'cancelled' | 'canceled',
  updatedMessage?: string
) => {
  try {
    // Normaliza status para respeitar a check constraint 'trade_requests_status_check' no banco
    const normalizedStatus = status === 'declined' ? 'rejected' : status === 'canceled' ? 'cancelled' : status;

    const payload: Record<string, any> = { status: normalizedStatus };
    if (typeof updatedMessage === 'string') {
      payload.message = updatedMessage;
    }

    const { data, error } = await supabase
      .from('trade_requests')
      .update(payload)
      .eq('id', requestId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating trade request status:', error);
    throw error;
  }
};


